import {
  CLEAR_FLIGHT_RESULT,
  CLEAR_FLIGHT_SEARCH_CACHE,
  UPDATE_FLIGHT_FILTERS,
  UPDATE_FLIGHTS_SORT,
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  FETCH_POPULAR_AIRPORTS,
  SET_FLIGHTS_CREDIT_DATA,
  FETCH_AIRLINES,
  FETCH_FLIGHT_CREDIT_RESERVATION,
  FETCH_FLIGHT_DEALS,
  FETCH_FLIGHT_DEAL,
  FETCH_FLIGHT_CAMPAIGNS,
  FETCH_FLIGHT_CAMPAIGN_DEALS,
  FETCH_FLIGHT_CAMPAIGN,
  UPDATE_FLIGHT_TRAVELLERS,
  UPDATE_FLIGHT_SEAT_SELECTIONS,
  CLEAR_FLIGHT_SEAT_SELECTIONS,
  FLIGHTS_DATE_CHANGE_SET_STEP,
  FLIGHTS_DATE_CHANGE_SELECTED_LEGS_TO_CHANGE,
  FLIGHTS_DATE_CHANGE_NEW_LEGS_WITH_DATES,
  FLIGHTS_DATE_CHANGE_RESET,
  CHECKOUT_PROCESS_SUCCESS,
} from 'actions/actionConstants'
import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import { arrayToObject, sortBy } from 'lib/array/arrayUtils'
import {
  FETCH_FLIGHT_JOURNEY,
  FETCH_FLIGHTS,
  FETCH_FLIGHT_PRICE,
  FETCH_FLIGHT_CALENDAR,
  FETCH_FLIGHT_DATE_GRID,
  FETCH_SEARCH_FLIGHTS,
  FETCH_FLIGHT_FARE_FAMILIES,
  FETCH_RECENT_FLIGHT_SEARCHES,
  FETCH_FLIGHTS_DATE_CHANGE_FLIGHTS_LIST,
  FETCH_FLIGHT_SEATS,
  FETCH_FLIGHT_PENALTIES_DETAILS,
  FETCH_FARE_WATCHES,
  DELETE_FARE_WATCH,
  FETCH_FARE_WATCH_STATUS,
  FETCH_FARESENSE_SUGGESTIONS,
} from 'actions/apiActionConstants'
import { FlightChangeDatesPageStep } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/flights'

const initialState: App.FlightsState = {
  // Old booking fields, do not use
  journeys: [],
  filters: [],
  flightSearchV2: {
    onewayFares: [],
    searchId: undefined,
  },
  sort: 'fastest',
  isFetching: false,
  isFetched: false,
  hasError: false,
  fetchedKey: '',
  errorMessage: '',
  // Actual fields
  journeysById: {},
  isBaggageFetching: false,
  isBaggageFetchingError: false,
  flightPricesLoading: {},
  flightPrices: {},
  fareFamiliesByFlight: {},
  flightCalendarLoading: {},
  flightCalendar: {},
  flightDateGrid: {},
  flightDateGridLoading: {},
  searchFlights: {},
  searchV2Flights: {},
  journeyFetching: {},
  journeyFeederOriginAirportCodeByKey: {},
  journeyErrors: {},
  popularAirports: {
    origins: [],
    destinations: [],
    fetching: false,
  },
  creditDetails: undefined,
  creditRebookingReservation: undefined,
  isCreditReservationFetching: false,
  isCreditReservationError: false,
  flightDeals: {},
  flightDealLists: {},
  campaigns: {},
  campaign: {},
  campaignDeals: {},
  airlines: {},
  isUpdatingTravellers: false,
  hasUpdatedTravellers: false,
  recentFlightSearches: {
    data: undefined,
    fetching: false,
    error: undefined,
  },
  flightsDateChangeSearch: {},
  flightsDateChange: {
    currentStep: FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE,
    selectedLegsToChange: [],
    selectedLegWithNewDepartureDate: [],
  },
  seatsMap: {},
  seatSelections: {},
  penalties: {},
  fareWatches: {
    data: [],
    fetching: false,
    error: undefined,
  },
  searchFareWatchMap: {},
  fareSenseSuggestions: {},
}

const apiCalls = apiReducerSwitch<App.FlightsState>({
  [FETCH_AIRLINES]: {
    request: (state) => ({
      airlines: { ...state.airlines },
    }),
    success: (state, action) => {
      // The GET airlines endpoint is also filtering the airline by names, when
      // the code given, so in order to not fetch again for every single airline
      // we are transforming the response array into object and merging it with
      // the state.
      const fetchedAirlines = arrayToObject(action.data, (airline) => airline.airlineCode)
      const airlines = { ...state.airlines }

      Object.entries(fetchedAirlines).forEach(([key, value]) => {
        airlines[key] = { data: value, fetching: false }
      })

      return { airlines }
    },
    failure: (state) => ({
      airlines: { ...state.airlines },
    }),
  },
  [FETCH_FLIGHT_JOURNEY]: {
    request: (state, action) => ({
      journeyFetching: { ...state.journeyFetching, [action.key]: true },
      journeyFeederOriginAirportCodeByKey: {
        ...(state.journeyFeederOriginAirportCodeByKey ?? {}),
        [action.key]: action.feederOriginAirportCode,
      },
    }),
    success: (state, action) => {
      if (state.journeyFeederOriginAirportCodeByKey?.[action.key] !== action.feederOriginAirportCode) {
        return undefined
      }

      return {
        journeysById: {
          ...state.journeysById,
          [action.key]: action.data,
        },
        journeyFetching: { ...state.journeyFetching, [action.key]: false },
      }
    },
    failure: (state, action) => {
      if (state.journeyFeederOriginAirportCodeByKey?.[action.key] !== action.feederOriginAirportCode) {
        return undefined
      }

      return {
        journeyErrors: { ...state.journeyErrors, [action.key]: action.error.message },
        journeyFetching: { ...state.journeyFetching, [action.key]: false },
      }
    },
  },
  [FETCH_FLIGHTS]: {
    request: (state, action) => ({
      journeys: [],
      fetchedKey: action.key,
      isFetching: true,
      isFetched: false,
    }),
    success: (state, action) => ({
      isFetching: false,
      isFetched: true,
      journeys: 'journeys' in action.data ? action.data.journeys : [],
      ...('onewayFares' in action.data && {
        flightSearchV2: {
          onewayFares: action.data.onewayFares,
          searchId: action.data.searchId,
        },
      }),
      hasError: false,
      errorMessage: '',
    }),
    failure: (state, action) => ({
      hasError: true,
      isFetching: false,
      errorMessage: action.error.message,
    }),
  },
  [FETCH_SEARCH_FLIGHTS]: {
    request: (state, action) => ({
      searchFlights: {
        ...state.searchFlights,
        [action.key]: {
          journeys: [],
          fetching: true,
        },
      },
      searchV2Flights: {
        ...state.searchV2Flights,
        [action.key]: {
          fares: [],
          fetching: true,
        },
      },
    }),
    success: (state, action) => {
      if (action.data.onewayFares) {
        return {
          searchFlights: {
            ...state.searchFlights,
            [action.key]: {
              journeys: [], // This line must be BEFORE the spread of state.searchFlights[action.key]
              ...state.searchFlights[action.key],
              fetching: false,
            },
          },
          searchV2Flights: {
            ...state.searchV2Flights,
            [action.key]: {
              fares: action.data.onewayFares,
              fetching: false,
              searchId: action.data.searchId,
              sessionId: action.data.sessionId,
              viewType: action.data.viewType,
              fnfEligible: action.data.fnfEligible,
              metadata: action.data.metadata,
              timestamp: action.data.timestamp,
              combinedJourneys: action.data.combinedJourneys,
            },
          },
        }
      } else {
        return {
          searchFlights: {
            ...state.searchFlights,
            [action.key]: {
              journeys: action.data.journeys ?? [],
              viewType: action.data.viewType,
              fetching: false,
            },
          },
          searchV2Flights: {
            ...state.searchV2Flights,
            [action.key]: {
              fares: [], // This line must be BEFORE the spread of state.searchV2Flights[action.key]
              ...state.searchV2Flights[action.key],
              fetching: false,
            },
          },
          sort: action.data.sort,
        }
      }
    },
    failure: (state, action) => ({
      searchFlights: {
        ...state.searchFlights,
        [action.key]: {
          journeys: [], // This line must be BEFORE the spread of state.searchFlights[action.key]
          ...state.searchFlights[action.key],
          fetching: false,
          error: action.error,
        },
      },
      searchV2Flights: {
        ...state.searchV2Flights,
        [action.key]: {
          fares: [], // This line must be BEFORE the spread of state.searchV2Flights[action.key]
          ...state.searchV2Flights[action.key],
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_FLIGHT_CALENDAR]: {
    request: (state, action) => ({
      flightCalendarLoading: {
        ...state.flightCalendarLoading,
        [action.key]: true,
      },
    }),
    success: (state, action) => ({
      flightCalendar: {
        ...state.flightCalendar,
        [action.key]: action.data,
      },
      flightCalendarLoading: {
        ...state.flightCalendarLoading,
        [action.key]: false,
      },
    }),
    failure: (state, action) => ({
      flightCalendarLoading: {
        ...state.flightCalendarLoading,
        [action.key]: false,
      },
    }),
  },
  [FETCH_FLIGHT_DATE_GRID]: {
    request: (state, action) => ({
      flightDateGridLoading: {
        ...state.flightDateGridLoading,
        [action.key]: true,
      },
    }),
    success: (state, action) => ({
      flightDateGrid: {
        ...state.flightDateGrid,
        [action.key]: action.data,
      },
      flightDateGridLoading: {
        ...state.flightDateGridLoading,
        [action.key]: false,
      },
    }),
    failure: (state, action) => ({
      flightDateGridLoading: {
        ...state.flightDateGridLoading,
        [action.key]: false,
      },
    }),
  },
  [FETCH_FLIGHT_PRICE]: {
    request: (state, action) => ({
      flightPricesLoading: {
        ...state.flightPricesLoading,
        [action.key]: true,
      },
    }),
    success: (state, action) => ({
      flightPrices: {
        ...state.flightPrices,
        [action.key]: action.data,
      },
      flightPricesLoading: {
        ...state.flightPricesLoading,
        [action.key]: false,
      },
    }),
    failure: (state, action) => ({
      flightPricesLoading: {
        ...state.flightPricesLoading,
        [action.key]: false,
      },
    }),
  },
  [FETCH_FLIGHT_FARE_FAMILIES]: {
    request: (state, action) => ({
      fareFamiliesByFlight: {
        ...state.fareFamiliesByFlight,
        [action.key]: {
          fetching: true,
          families: [],
          familiesPerLeg: [],
        },
      },
    }),
    success: (state, action) => ({
      fareFamiliesByFlight: {
        ...state.fareFamiliesByFlight,
        [action.key]: {
          fetching: false,
          families: action.data.upsellFares,
          familiesPerLeg: action.data.upsellFaresPerLeg ?? [],
          isMixedUpsell: action.data.isMixedUpsell,
        },
      },
    }),
    failure: (state, action) => ({
      fareFamiliesByFlight: {
        ...state.fareFamiliesByFlight,
        [action.key]: {
          fetching: false,
          error: action.error,
          families: [],
          familiesPerLeg: [],
        },
      },
    }),
  },
  [FETCH_FLIGHT_CREDIT_RESERVATION]: {
    request: () => ({
      isCreditReservationFetching: true,
      creditRebookingReservation: undefined,
      isCreditReservationError: false,
    }),
    success: (state, action) => ({
      creditRebookingReservation: action.data,
      isCreditReservationFetching: false,
      isCreditReservationError: false,
    }),
    failure: () => ({
      isCreditReservationFetching: false,
      isBaggageFetchingError: true,
    }),
  },
  [FETCH_FLIGHT_DEALS]: {
    request: (state, action) => ({
      flightDealLists: {
        ...state.flightDealLists,
        [action.key]: {
          dealIds: [],
          error: undefined,
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      flightDeals: {
        ...state.flightDeals,
        ...arrayToObject(
          action.data,
          deal => deal.id,
          // if we already have it, don't overwrite - the singular endpoint returns more data at the moment
          deal => ({
            error: undefined,
            fetching: false,
            deal,
          }),
        ),
      },
      flightDealLists: {
        ...state.flightDealLists,
        [action.key]: {
          dealIds: action.data?.map(deal => deal.id) ?? [],
          error: undefined,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      flightDealLists: {
        ...state.flightDealLists,
        [action.key]: {
          dealIds: [],
          error: action.error,
          fetching: false,
        },
      },
    }),
  },
  [FETCH_FLIGHT_CAMPAIGNS]: {
    request: (state, action) => ({
      campaigns: {
        ...state.campaigns,
        [action.key]: {
          data: [],
          error: undefined,
          fetching: true,
          isEnabled: false,
        },
      },
    }),
    success: (state, action) => ({
      campaigns: {
        ...state.campaigns,
        [action.key]: {
          data: action.data.rows,
          error: undefined,
          fetching: false,
          isEnabled: action.data.isEnabled,
        },
      },
    }),
    failure: (state, action) => ({
      campaigns: {
        ...state.campaigns,
        [action.key]: {
          data: [],
          error: action.error,
          fetching: false,
          isEnabled: false,
        },
      },
    }),
  },
  [FETCH_FLIGHT_CAMPAIGN]: {
    request: (state, action) => ({
      campaign: {
        ...state.campaign,
        [action.key]: {
          data: undefined,
          error: undefined,
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      campaign: {
        ...state.campaign,
        [action.key]: {
          data: action.data,
          error: undefined,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      campaign: {
        ...state.campaign,
        [action.key]: {
          data: undefined,
          error: action.error,
          fetching: false,
        },
      },
    }),
  },
  [FETCH_FLIGHT_CAMPAIGN_DEALS]: {
    request: (state, action) => ({
      campaignDeals: {
        ...state.campaignDeals,
        [action.key]: {
          data: [],
          error: undefined,
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      campaignDeals: {
        ...state.campaignDeals,
        [action.key]: {
          data: action.data,
          error: undefined,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      campaignDeals: {
        ...state.campaignDeals,
        [action.key]: {
          data: [],
          error: action.error,
          fetching: false,
        },
      },
    }),
  },
  [FETCH_FLIGHT_DEAL]: {
    request: (state, action) => ({
      flightDeals: {
        ...state.flightDeals,
        [action.id]: {
          error: undefined,
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      flightDeals: {
        ...state.flightDeals,
        [action.id]: {
          error: undefined,
          fetching: false,
          deal: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      flightDeals: {
        ...state.flightDeals,
        [action.id]: {
          error: action.error,
          fetching: false,
        },
      },
    }),
  },
  [FETCH_POPULAR_AIRPORTS]: {
    request: (state) => ({
      popularAirports: {
        ...state.popularAirports,
        fetching: true,
      },
    }),
    success: (state, action) => ({
      popularAirports: {
        ...action.data,
        fetching: false,
      },
    }),
  },
  [UPDATE_FLIGHT_TRAVELLERS]: {
    request: () => ({
      isUpdatingTravellers: true,
      hasUpdatedTravellers: false,
    }),
    success: () => ({
      isUpdatingTravellers: false,
      hasUpdatedTravellers: true,
    }),
    failure: () => ({
      isUpdatingTravellers: false,
      hasUpdatedTravellers: true,
    }),
  },
  [FETCH_RECENT_FLIGHT_SEARCHES]: {
    request: (state) => ({
      recentFlightSearches: {
        ...state.recentFlightSearches,
        fetching: true,
        error: undefined,
      },
    }),
    success: (state, action) => ({
      recentFlightSearches: {
        data: action.data,
        fetching: false,
        error: undefined,
      },
    }),
    failure: (state, action) => ({
      recentFlightSearches: {
        ...state.recentFlightSearches,
        fetching: false,
        error: action.error,
      },
    }),
  },
  [FETCH_FLIGHTS_DATE_CHANGE_FLIGHTS_LIST]: {
    request: (state, action) => ({
      flightsDateChangeSearch: {
        ...state.flightsDateChangeSearch,
        [action.key]: {
          fares: [],
          fareIdsToUpsellFarePerLeg: {},
          isFetching: true,
        },
      },
    }),
    success: (state, action) => ({
      flightsDateChangeSearch: {
        ...state.flightsDateChangeSearch,
        [action.key]: {
          fares: action.data.onewayFares,
          fareIdsToUpsellFarePerLeg: action.data.fareIdsToUpsellFarePerLeg,
          searchId: action.data.searchId,
          isFetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      flightsDateChangeSearch: {
        ...state.flightsDateChangeSearch,
        [action.key]: {
          fares: [],
          fareIdsToUpsellFarePerLeg: {},
          isFetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_FLIGHT_SEATS]: {
    request: (state, action) => ({
      seatsMap: {
        ...state.seatsMap,
        [action.key]: { data: undefined, fetching: true },
      },
    }),
    success: (state, action) => ({
      seatsMap: {
        ...state.seatsMap,
        [action.key]: { data: action.data, fetching: false },
      },
    }),
    failure: (state, action) => ({
      seatsMap: {
        ...state.seatsMap,
        [action.key]: { data: undefined, fetching: false, error: action.error },
      },
    }),
  },
  [FETCH_FLIGHT_PENALTIES_DETAILS]: {
    request: (state, action) => ({
      penalties: {
        ...state.penalties,
        [action.key]: { data: undefined, fetching: true },
      },
    }),
    success: (state, action) => ({
      penalties: {
        ...state.penalties,
        [action.key]: { data: action.data, fetching: false },
      },
    }),
    failure: (state, action) => ({
      penalties: {
        ...state.penalties,
        [action.key]: { data: undefined, fetching: false, error: action.error },
      },
    }),
  },
  [FETCH_FARE_WATCHES]: {
    request: (state) => ({
      fareWatches: { ...state.fareWatches, fetching: true, error: undefined },
    }),
    success: (_state, action) => ({
      fareWatches: { data: action.data, fetching: false, error: undefined },
    }),
    failure: (state, action) => ({
      fareWatches: { ...state.fareWatches, fetching: false, error: action.error },
    }),
  },
  [DELETE_FARE_WATCH]: {
    success: (state, action) => {
      const updatedMap = { ...state.searchFareWatchMap }
      for (const key of Object.keys(updatedMap)) {
        if (updatedMap[key]?.id === action.fareWatchId) {
          updatedMap[key] = { active: false, fetching: false }
        }
      }
      return {
        fareWatches: {
          ...state.fareWatches,
          data: state.fareWatches.data.filter((fw) => fw.id !== action.fareWatchId),
        },
        searchFareWatchMap: updatedMap,
      }
    },
  },
  [FETCH_FARE_WATCH_STATUS]: {
    request: (state, action) => ({
      searchFareWatchMap: {
        ...state.searchFareWatchMap,
        [action.key]: { active: false, fetching: true },
      },
    }),
    success: (state, action) => ({
      searchFareWatchMap: {
        ...state.searchFareWatchMap,
        [action.key]: {
          active: action.data.active,
          id: action.data.id,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      searchFareWatchMap: {
        ...state.searchFareWatchMap,
        [action.key]: { active: false, fetching: false },
      },
    }),
  },
  [FETCH_FARESENSE_SUGGESTIONS]: {
    request: (state, action) => ({
      fareSenseSuggestions: {
        ...state.fareSenseSuggestions,
        [action.key]: { fetching: true },
      },
    }),
    success: (state, action) => ({
      fareSenseSuggestions: {
        ...state.fareSenseSuggestions,
        [action.key]: { fetching: false, data: action.data },
      },
    }),
    failure: (state, action) => ({
      fareSenseSuggestions: {
        ...state.fareSenseSuggestions,
        [action.key]: { fetching: false, error: action.error },
      },
    }),
  },
})

const flightsReducer = createAppReducer<App.FlightsState>(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [CLEAR_FLIGHT_RESULT]: () => ({
    journeys: [],
    filters: [],
    flightSearchV2: {
      onewayFares: [],
      searchId: undefined,
    },
    sort: 'fastest',
    isFetching: false,
    isFetched: false,
    hasError: false,
    errorMessage: '',
    isBaggageFetching: false,
    isBaggageFetchingError: false,
  }),
  [CLEAR_FLIGHT_SEARCH_CACHE]: () => ({
    searchFlights: {},
    searchV2Flights: {},
    journeysById: {},
    fareFamiliesByFlight: {},
  }),
  [UPDATE_FLIGHT_FILTERS]: (state, action) => ({
    filters: sortBy(action.data, (f: App.FlightFilter<App.AnyJourney>) => f.order, 'asc'),
  }),
  [UPDATE_FLIGHTS_SORT]: (state, action) => ({
    sort: action.data,
  }),
  [SET_FLIGHTS_CREDIT_DATA]: (state, action) => ({
    creditDetails: action.data,
  }),
  [FLIGHTS_DATE_CHANGE_SET_STEP]: (state, action) => ({
    ...state,
    flightsDateChange: {
      ...state.flightsDateChange,
      currentStep: action.step,
    },
  }),
  [FLIGHTS_DATE_CHANGE_SELECTED_LEGS_TO_CHANGE]: (state, action) => ({
    ...state,
    flightsDateChange: {
      ...state.flightsDateChange,
      selectedLegsToChange: action.selectedLegsToChange,
    },
  }),
  [FLIGHTS_DATE_CHANGE_NEW_LEGS_WITH_DATES]: (state, action) => ({
    ...state,
    flightsDateChange: {
      ...state.flightsDateChange,
      selectedLegWithNewDepartureDate: action.selectedLegsWithDates,
    },
  }),
  [FLIGHTS_DATE_CHANGE_RESET]: (state: App.FlightsState) => ({
    ...state,
    flightsDateChange: {
      currentStep: FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE,
      selectedLegsToChange: [],
      selectedLegWithNewDepartureDate: [],
    },
  }),
  [UPDATE_FLIGHT_SEAT_SELECTIONS]: (state, action) => ({
    ...state,
    seatSelections: {
      ...state.seatSelections,
      [action.key]: action.seatSelections,
    },
  }),
  [CLEAR_FLIGHT_SEAT_SELECTIONS]: (state) => ({
    ...state,
    seatSelections: {},
    seatsMap: {},
  }),
  [CHECKOUT_PROCESS_SUCCESS]: (state: App.FlightsState) => ({
    ...state,
    flightsDateChange: {
      currentStep: FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE,
      selectedLegsToChange: [],
      selectedLegWithNewDepartureDate: [],
    },
    flightsDateChangeSearch: {},
  }),
})

export default flightsReducer
